import { Component, OnInit } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { AuthenticationService } from '../../../../lib/services/authentication.service';
import { Router } from '@angular/router';
import { LocalSettingsService } from '../../../../lib/services/local-settings.service';
import { AccessRight, UserInfoApi } from '../../../../lib/models/oAuth.model';
import { VersionType } from '../../../../lib/services/access-matrix.service';
import { LoginService } from '../../../../lib/services/login.service';
import { PersistenceService } from '../../../shared/persistence/services/persistence.service';
import { LoggerService } from '../../../shared/logger/services/logger.service';
import { getIdToken, signOut } from 'firebase/auth';

@Component({
  selector: 'ms-o-auth-login',
  templateUrl: './o-auth-login.component.html',
  styleUrls: ['./o-auth-login.component.scss'],
  standalone: false,
})
export class OAuthLoginComponent implements OnInit {
  private static readonly unauthorizedMessageKey = 'unauthorized-user';
  private static readonly medispringDomain = '@medispring.be';

  public userInfoApi: UserInfoApi | null = null;
  public accessRight: AccessRight | null = null;
  public isLoading: boolean = false;

  constructor(
    private firebaseAuth: Auth,
    private auth: AuthenticationService,
    private router: Router,
    private localSettings: LocalSettingsService,
    private loginService: LoginService,
    private persistenceService: PersistenceService,
    private logger: LoggerService,
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    void this.initialized();
  }

  private async initialized() {
    try {
      const currentUser = this.firebaseAuth.currentUser;
      if (!currentUser?.email) {
        await this.handleInitializationFailure(false);
        return;
      }

      const idToken = await getIdToken(currentUser, true);
      this.localSettings.oauthIdToken = idToken;
      this.userInfoApi = {
        id: currentUser.uid,
        email: currentUser.email,
        verified_email: currentUser.emailVerified,
        name: currentUser.displayName ?? undefined,
        given_name: currentUser.displayName ?? undefined,
        family_name: undefined,
        picture: currentUser.photoURL ?? undefined,
        locale: undefined,
        hd: currentUser.email?.split('@')[1],
      };

      if (this.userInfoApi.verified_email === true) {
        await this.getAccessRight();
      } else {
        await this.handleInitializationFailure(true);
      }
    } catch (error) {
      this.logger.error('Error while initializing Firebase auth', error);
      await this.handleInitializationFailure(true);
    }
  }

  private async handleInitializationFailure(
    shouldSignOut: boolean,
  ): Promise<void> {
    this.isLoading = false;
    if (shouldSignOut) {
      await signOut(this.firebaseAuth);
    }
    await this.router.navigate(['login']);
  }

  private async getAccessRight() {
    try {
      const userEmail = this.userInfoApi?.email;
      if (!userEmail) {
        this.isLoading = false;
        this.router.navigate(['login']);
        return;
      }

      const accessRight =
        await this.persistenceService.getAccessRight(userEmail);
      if (accessRight) {
        this.accessRight = accessRight;
        this.localSettings.setAccessRight(this.accessRight);
        this.isLoading = false;
      } else if (
        userEmail
          .toLocaleLowerCase()
          .endsWith(OAuthLoginComponent.medispringDomain)
      ) {
        this.accessRight = {
          user: userEmail,
          rights: [
            {
              version: VersionType.FULL,
              options: [],
            },
            {
              version: VersionType.FREE,
              options: [],
            },
          ],
        };
        this.localSettings.setAccessRight(this.accessRight);
        this.isLoading = false;
      } else {
        this.isLoading = false;
        this.router.navigate(['login'], {
          queryParams: {
            unauthorized: OAuthLoginComponent.unauthorizedMessageKey,
          },
        });
      }
    } catch (e) {
      this.logger.error('Error while loading access rights', e);
      this.isLoading = false;
      this.router.navigate(['login'], {
        queryParams: {
          unauthorized: OAuthLoginComponent.unauthorizedMessageKey,
        },
      });
    }
  }

  public async login(type: VersionType) {
    const currentUser = this.firebaseAuth.currentUser;
    if (!currentUser?.email) {
      return;
    }

    const credentials = await this.persistenceService.getCredentials(type);
    if (!credentials) {
      return;
    }

    const user = await this.auth.login(credentials);
    if (user) {
      this.localSettings.setBasicAuth(
        btoa(credentials.username + ':' + credentials.password),
      );
      this.localSettings.setVersion(type);
      this.localSettings.setListOfGroupIds([]);
      await this.loginService.redirectConnectedUser(user, 'private');
    }
  }
}
